import { useEffect, useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Input from "@/v2-ui/input"
import Switch from "@/v2-ui/switch"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Button from "@/v2-backoffice-client/components/button"
import { useUptimeConfigProviderUpsertMutation } from "@/v2-backoffice-client/uptime/configProvider/upsert/uptime.configProvider.upsert.mutation"
import type { UptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/__types__/uptime.config.query"

type Provider = UptimeConfigQuery["getStatusConfig"]["Providers"][number]

export type UptimeConfigProviderDialogProps = DialogProps & {
  mode: "create" | "edit"
  provider?: Provider | null
}

const FORM_ID = "uptime-config-provider-form"

function makeInitial(provider: Provider | null | undefined) {
  return {
    id: provider?.Id ?? "",
    name: provider?.Name ?? "",
    priority: provider?.Priority ?? 0,
    isEnabled: provider?.IsEnabled ?? true,
  }
}

function UptimeConfigProviderDialog(props: UptimeConfigProviderDialogProps) {
  const { mode, provider, open, onOpenChange, ...dialogProps } = props

  const [form, setForm] = useState(() => makeInitial(provider))
  const [upsert, { loading, error, reset }] =
    useUptimeConfigProviderUpsertMutation({
      onCompleted() {
        onOpenChange?.(false)
      },
    })

  useEffect(() => {
    if (open) {
      setForm(makeInitial(provider))
      reset()
    }
  }, [open, provider, reset])

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            {mode === "edit" ? "Edit provider" : "New provider"}
          </Dialog.Title>
          <Dialog.Description>
            Vendor name shown next to each public-status service.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-3" error={error} />

        <form
          id={FORM_ID}
          className="uptime-config-form"
          onSubmit={(e) => {
            e.preventDefault()
            upsert({
              variables: {
                input: {
                  id: form.id.trim(),
                  name: form.name.trim(),
                  isEnabled: form.isEnabled,
                  priority: form.priority,
                },
              },
            })
          }}
        >
          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-provider-id">ID</Label>
            <Input
              id="uptime-config-provider-id"
              type="text"
              required
              autoFocus={mode === "create"}
              disabled={mode === "edit"}
              placeholder="e.g. geodata"
              value={form.id}
              onChange={(e) => {
                setForm({ ...form, id: e.target.value })
              }}
            />
            <p className="uptime-config-form-hint">
              Stable kebab-case key. Cannot be changed after create.
            </p>
          </div>

          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-provider-name">Display name</Label>
            <Input
              id="uptime-config-provider-name"
              type="text"
              required
              placeholder="e.g. Geodata"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value })
              }}
            />
          </div>

          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-provider-priority">Priority</Label>
            <Input
              id="uptime-config-provider-priority"
              type="number"
              required
              value={form.priority}
              onChange={(e) => {
                setForm({
                  ...form,
                  priority: Number.parseInt(e.target.value, 10) || 0,
                })
              }}
            />
            <p className="uptime-config-form-hint">
              Lower priority wins on suffix ties.
            </p>
          </div>

          <Label
            className="uptime-config-form-toggle"
            htmlFor="uptime-config-provider-enabled"
          >
            <span>Enabled</span>
            <Switch
              id="uptime-config-provider-enabled"
              checked={form.isEnabled}
              onCheckedChange={(checked) => {
                setForm({ ...form, isEnabled: checked === true })
              }}
            />
          </Label>
        </form>

        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} isLoading={loading}>
            {mode === "edit" ? "Save" : "Create"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default UptimeConfigProviderDialog
